import React, { useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { createUpload, deleteUpload, fetchFoodLogs, fetchFoodLog, fetchPartners, fetchPartner } from "api";
import { PageHeader, Table, Spinner, EmptyState } from "components/common";
import { useAuth } from "hooks/useAuth";
import type { FoodLog, Upload } from "types";

// ─── Logs list ────────────────────────────────────────────────────────────────

export function LogsPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const filterPartnerId = searchParams.get("partner_entity_id");

  const { data: logs = [], isLoading } = useQuery({
    queryKey: ["foodLogs", filterPartnerId],
    queryFn:  () => fetchFoodLogs(filterPartnerId ? { partner_entity_id: filterPartnerId } : undefined),
  });

  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const partnerNames = React.useMemo(() => {
    const map: Record<number, string> = {};
    partners.forEach((p) => { map[p.id] = p.name; });
    return map;
  }, [partners]);

  const filterPartnerName = filterPartnerId ? partnerNames[Number(filterPartnerId)] : null;

  if (isLoading) return <Spinner className="mt-20" />;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8">
      <PageHeader
        title="Food logs"
        subtitle={filterPartnerName ? `${logs.length} entries for ${filterPartnerName}` : `${logs.length} entries`}
        action={
          <button className="btn-primary" onClick={() => navigate("/logs/new")}>
            Add log
          </button>
        }
      />

      {filterPartnerId && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-sm text-gray-500">
            Filtered by: <span className="font-medium text-gray-700">{filterPartnerName ?? `Partner #${filterPartnerId}`}</span>
          </span>
          <button
            onClick={() => navigate("/logs")}
            className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
          >
            ✕ Clear filter
          </button>
        </div>
      )}

      {/* ── Stats tiles ────────────────────────────────────────────────────── */}
      {logs.length > 0 && (
        <div className="grid grid-cols-2 gap-4 mb-6">
          <div className="card px-5 py-4">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Total food rescued</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">
              {logs.reduce((sum, l) => sum + l.poundage, 0).toLocaleString()} <span className="text-sm font-normal text-gray-400">lbs</span>
            </p>
          </div>
          <div className="card px-5 py-4">
            <p className="text-xs font-medium text-gray-400 uppercase tracking-wide">Partners</p>
            <p className="text-2xl font-semibold text-gray-900 mt-1">
              {new Set(logs.map((l) => l.partner_entity_id)).size}
            </p>
          </div>
        </div>
      )}

      <div className="card">
        {logs.length === 0 ? (
          <EmptyState message="No food logs yet." />
        ) : (
          <Table<FoodLog>
            data={logs}
            keyExtractor={(l) => l.id}
            onRowClick={(l) => navigate(`/logs/${l.id}`)}
            columns={[
              {
                header:   "Date",
                accessor: (l) => format(parseISO(l.created_at), "MMM d, yyyy"),
              },
              {
                header:   "Partner",
                accessor: (l) => partnerNames[l.partner_entity_id] ? (
                  <button
                    className="text-brand-600 hover:underline text-sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      navigate(`/partners/${l.partner_entity_id}`);
                    }}
                  >
                    {partnerNames[l.partner_entity_id]}
                  </button>
                ) : "—",
              },
              {
                header:   "Receiving Partner",
                accessor: (l) => l.receiving_partner_id != null ? (partnerNames[l.receiving_partner_id] ?? "—") : "—",
              },
              {
                header:   "Poundage",
                accessor: (l) => `${l.poundage} lbs`,
              },
              {
                header:   "Est. retail value",
                accessor: (l) =>
                  l.estimated_retail_value != null
                    ? `$${l.estimated_retail_value.toFixed(2)}`
                    : "—",
              },
              {
                header:   "Pickup",
                accessor: (l) =>
                  l.schedule_item_id ? (
                    <button
                      className="text-xs text-brand-600 hover:text-brand-700 font-medium"
                      title={`View pickup #${l.schedule_item_id}`}
                      onClick={(e) => {
                        e.stopPropagation();
                        navigate(`/schedule-items/${l.schedule_item_id}`);
                      }}
                    >
                      view
                    </button>
                  ) : (
                    <span className="text-gray-200">—</span>
                  ),
              },
            ]}
          />
        )}
      </div>
    </div>
  );
}

// ─── Log detail ───────────────────────────────────────────────────────────────

export function LogDetailPage() {
  const { id }      = useParams<{ id: string }>();
  const navigate    = useNavigate();
  const queryClient = useQueryClient();
  const { isAdmin, isCourier } = useAuth();
  const logId       = Number(id);

  const fileInputRef  = useRef<HTMLInputElement>(null);
  const [lightbox, setLightbox] = useState<Upload | null>(null);

  const { data: log, isLoading } = useQuery({
    queryKey: ["foodLogs", logId],
    queryFn:  () => fetchFoodLog(logId),
  });

  const { data: partner } = useQuery({
    queryKey: ["partners", log?.partner_entity_id],
    queryFn:  () => fetchPartner(log!.partner_entity_id),
    enabled:  !!log?.partner_entity_id,
  });

  const { data: partners = [] } = useQuery({
    queryKey: ["partners"],
    queryFn:  fetchPartners,
  });

  const partnerNames = React.useMemo(() => {
    const map: Record<number, string> = {};
    partners.forEach((p) => { map[p.id] = p.name; });
    return map;
  }, [partners]);


  const uploadMutation = useMutation({
    mutationFn: (file: File) => createUpload("FoodLog", logId, file),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ["foodLogs", logId] }),
  });

  const deleteMutation = useMutation({
    mutationFn: (uploadId: number) => deleteUpload(uploadId),
    onSuccess:  () => queryClient.invalidateQueries({ queryKey: ["foodLogs", logId] }),
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    files.forEach((file) => uploadMutation.mutate(file));
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  if (isLoading) return <Spinner className="mt-20" />;
  if (!log)      return <EmptyState message="Log not found." />;

  const uploads = log.uploads ?? [];
  const canEdit = isAdmin || isCourier;

  return (
    <div className="px-4 py-6 sm:px-8 sm:py-8 max-w-2xl">
      {/* Lightbox */}
      {lightbox && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80"
          onClick={() => setLightbox(null)}
        >
          <img
            src={lightbox.link}
            alt="Full size"
            className="max-h-[90vh] max-w-[90vw] rounded-lg object-contain"
            onClick={(e) => e.stopPropagation()}
          />
          <button
            onClick={() => setLightbox(null)}
            className="absolute top-4 right-4 text-white text-2xl leading-none hover:text-gray-300 transition-colors"
          >
            ✕
          </button>
        </div>
      )}

      <button
        onClick={() => navigate(-1)}
        className="text-sm text-gray-400 hover:text-gray-600 mb-6 flex items-center gap-1 transition-colors"
      >
        ← Back
      </button>

      <PageHeader
        title="Food log"
        subtitle={`Entry #${log.id}`}
        action={canEdit ? (
          <button className="btn-secondary" onClick={() => navigate(`/logs/${log.id}/edit`)}>
            Edit
          </button>
        ) : undefined}
      />

      <div className="card p-6 space-y-4">
        <Row
          label="Donating Partner"
          value={
            partner ? (
              <button
                className="text-brand-600 hover:underline text-sm"
                onClick={() => navigate(`/partners/${partner.id}`)}
              >
                {partner.name}
              </button>
            ) : (
              `Partner #${log.partner_entity_id}`
            )
          }
        />
        <Row label="Receiving Partner" value={
          log.receiving_partner_id ? 
          (
            <button
              onClick={() => navigate(`/partners/${log.receiving_partner_id}`)}
              className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
              >
              <span className={"px-1.5 py-0.5 rounded font-large bg-amber-100 text-amber-700"}>
                {partnerNames[log.receiving_partner_id]}
              </span>
            </button>
          )
          : (
            <span className="text-gray-400">No receiving partner recorded</span>
          )
        } />
        <Row label="Poundage" value={`${log.poundage} lbs`} />
        <Row
          label="Est. retail value"
          value={log.estimated_retail_value != null ? `$${log.estimated_retail_value.toFixed(2)}` : "—"}
        />
        <Row
          label="Completed at"
          value={log.completed_at
            ? parseISO(log.completed_at).toLocaleString([], {
                year: "numeric", month: "long", day: "numeric",
                hour: "numeric", minute: "2-digit", timeZoneName: "short",
              })
            : "—"
          }
        />
        <Row label="Description" value={log.description ?? "—"} />
        <Row
          label="Scheduled item"
          value={
            log.schedule_item_id ? (
              <button
                className="text-brand-600 hover:underline text-sm"
                onClick={() => navigate(`/schedule-items/${log.schedule_item_id}`)}
              >
                View pickup #{log.schedule_item_id}
              </button>
            ) : (
              <span className="text-gray-400">Not linked</span>
            )
          }
        />
      </div>

      {/* ── Photos ──────────────────────────────────────────────────────────── */}
      <div className="mt-6">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-gray-800">
            Photos {uploads.length > 0 && <span className="font-normal text-gray-400">({uploads.length})</span>}
          </h3>
          {canEdit && (
            <>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleFileChange}
              />
              <button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadMutation.isPending}
                className="flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 disabled:opacity-50 transition-colors"
              >
                {uploadMutation.isPending ? <Spinner className="h-3" /> : "+"} Add photo
              </button>
            </>
          )}
        </div>

        {uploads.length === 0 ? (
          <p className="text-xs text-gray-400">No photos attached.</p>
        ) : (
          <div className="grid grid-cols-3 gap-2">
            {uploads.map((upload) => (
              <div key={upload.id} className="group relative aspect-square rounded-lg overflow-hidden bg-gray-100">
                <img
                  src={upload.link}
                  alt="Log photo"
                  className="h-full w-full object-cover cursor-pointer transition-opacity group-hover:opacity-90"
                  onClick={() => setLightbox(upload)}
                />
                {canEdit && (
                  <button
                    onClick={() => deleteMutation.mutate(upload.id)}
                    disabled={deleteMutation.isPending}
                    className="absolute top-1 right-1 hidden group-hover:flex items-center justify-center h-5 w-5 rounded-full bg-black/60 text-white text-xs hover:bg-red-600 transition-colors"
                  >
                    ✕
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {uploadMutation.isError && (
          <p className="mt-2 text-xs text-red-500">Upload failed. Please try again.</p>
        )}
        {deleteMutation.isError && (
          <p className="mt-2 text-xs text-red-500">Delete failed. Please try again.</p>
        )}
      </div>
    </div>
  );
}

function Row({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex gap-4">
      <span className="text-sm text-gray-500 w-36 shrink-0">{label}</span>
      <span className="text-sm text-gray-900">{value}</span>
    </div>
  );
}